import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Motion";
import { api, imgSrc } from "@/lib/api";

const Tag = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.15em] border border-white/20 rounded-full px-3 py-1 text-white/60">
    {children}
  </span>
);

const Work = () => {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  return (
    <div className="pt-40 pb-24" data-testid="work-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Overline>Selected work</Overline>
        <MaskedLines
          as="h1"
          className="mt-8 font-sora font-extrabold tracking-tighter leading-[1.0] text-4xl sm:text-6xl lg:text-7xl"
          lines={["Work that works", "as hard as you do."]}
        />
        <Reveal delay={0.25}>
          <p className="mt-8 max-w-xl text-white/60 leading-relaxed">
            Every project is a partnership. Here's what happens when ambitious
            businesses meet deliberate design.
          </p>
        </Reveal>

        {projects === null ? (
          <div className="mt-24 flex justify-center" data-testid="work-loading">
            <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-brand-cyan animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <p className="mt-24 text-white/50" data-testid="work-empty">Projects coming soon.</p>
        ) : projects.length === 1 ? (
          /* ---------- FEATURED LAYOUT (single project) ---------- */
          <Reveal className="mt-20" data-testid="work-featured">
            <article className="group">
              <Link to={`/work/${projects[0].id}`} data-cursor="view" data-testid="work-featured-image-link" className="block">
                <div className="relative rounded-3xl overflow-hidden border border-white/10">
                  <div className="aspect-[16/8] overflow-hidden">
                    <img
                      src={imgSrc(projects[0].image_url)}
                      alt={projects[0].title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-transparent to-transparent" />
                </div>
              </Link>
              <div className="mt-10 grid md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                  <p className="text-xs uppercase tracking-[0.25em] text-gradient font-bold">Case study 001</p>
                  <h2 className="font-sora font-extrabold tracking-tighter text-4xl md:text-5xl mt-4" data-testid="work-featured-title">
                    <Link to={`/work/${projects[0].id}`} className="hover:text-brand-cyan transition-colors duration-300">
                      {projects[0].title}
                    </Link>
                  </h2>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {projects[0].tags.map((t) => <Tag key={t}>{t}</Tag>)}
                  </div>
                  {projects[0].live_url && (
                    <a
                      href={projects[0].live_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="work-featured-live-link"
                      className="group/link mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-cyan"
                    >
                      Visit live site
                      <ExternalLink className="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </a>
                  )}
                </div>
                <div className="md:col-span-7">
                  <p className="text-white/65 leading-relaxed text-base md:text-lg md:pt-10" data-testid="work-featured-desc">
                    {projects[0].description}
                  </p>
                </div>
              </div>
            </article>
          </Reveal>
        ) : (
          /* ---------- GRID LAYOUT (2+ projects) ---------- */
          <div className="mt-20 grid md:grid-cols-2 gap-8" data-testid="work-grid">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.12}>
                <article className="group" data-testid={`work-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Link to={`/work/${p.id}`} data-cursor="view" className="block rounded-2xl overflow-hidden border border-white/10 aspect-[4/3]">
                    <img
                      src={imgSrc(p.image_url)}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
                    />
                  </Link>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <h2 className="font-sora font-bold text-2xl tracking-tight">
                        <Link to={`/work/${p.id}`} className="hover:text-brand-cyan transition-colors duration-300">{p.title}</Link>
                      </h2>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {p.tags.map((t) => <Tag key={t}>{t}</Tag>)}
                      </div>
                    </div>
                    {p.live_url && (
                      <a
                        href={p.live_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit ${p.title}`}
                        className="shrink-0 w-11 h-11 rounded-full border border-white/20 flex items-center justify-center transition-colors duration-300 hover:bg-brand-blue hover:border-brand-blue"
                      >
                        <ArrowUpRight className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-white/55 mt-4 leading-relaxed">{p.description}</p>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Work;
